import { convexQuery, useConvexMutation } from '@convex-dev/react-query'
import { api } from '@cvx/_generated/api'
import type { Doc, Id } from '@cvx/_generated/dataModel'
import type { JobQuestionNode } from '@cvx/features/jobs/job_questions_db'
import type { DragEndEvent } from '@dnd-kit/core'
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Button } from '@rekrytering/ui/components/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@rekrytering/ui/components/dropdown-menu'
import { Input } from '@rekrytering/ui/components/input'
import { Textarea } from '@rekrytering/ui/components/textarea'
import { cn } from '@rekrytering/ui/lib/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { GenerateQuestionsPanel } from '@/features/jobs/components/generate-questions-panel'
import { TranslateButton } from '@/features/jobs/components/translate-button'

type FollowUpDraft = {
  readonly id: string
  text: string
  competency: string
}

type QuestionDraft = {
  readonly id: string
  text: string
  competency: string
  textTranslation: string
  textTranslationIsDraft: boolean
  followUps: FollowUpDraft[]
  followUpsExpanded: boolean
}

const createEmptyQuestion = (): QuestionDraft => ({
  id: crypto.randomUUID(),
  text: '',
  competency: '',
  textTranslation: '',
  textTranslationIsDraft: false,
  followUps: [],
  followUpsExpanded: false,
})

const createEmptyFollowUp = (): FollowUpDraft => ({
  id: crypto.randomUUID(),
  text: '',
  competency: '',
})

const fromNodes = (nodes: readonly JobQuestionNode[]): QuestionDraft[] =>
  nodes.map((node) => ({
    id: node._id,
    text: node.text,
    competency: node.competency ?? '',
    textTranslation: node.textTranslations?.['en'] ?? '',
    textTranslationIsDraft: false,
    followUpsExpanded: node.followUps.length > 0,
    followUps: node.followUps.map((f) => ({
      id: f._id,
      text: f.text,
      competency: f.competency ?? '',
    })),
  }))

type SortableQuestionProps = {
  readonly jobId: Id<'jobs'>
  readonly jobLanguage: string
  readonly question: QuestionDraft
  readonly index: number
  readonly isLocked: boolean
  readonly onUpdate: (id: string, patch: Partial<QuestionDraft>) => void
  readonly onRemove: (id: string) => void
  readonly onUpdateFollowUp: (
    questionId: string,
    followUpId: string,
    patch: Partial<FollowUpDraft>,
  ) => void
  readonly onAddFollowUp: (questionId: string) => void
  readonly onRemoveFollowUp: (questionId: string, followUpId: string) => void
  readonly onSaveToBank: (questionId: string) => void
}

const SortableQuestion = ({
  jobId,
  jobLanguage,
  question,
  index,
  isLocked,
  onUpdate,
  onRemove,
  onUpdateFollowUp,
  onAddFollowUp,
  onRemoveFollowUp,
  onSaveToBank,
}: SortableQuestionProps) => {
  const { t } = useTranslation()
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: question.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'rounded-lg border border-border bg-background p-4',
        isDragging && 'opacity-50',
      )}
    >
      <div className="flex gap-3">
        <button
          type="button"
          className={cn(
            'mt-2 touch-none text-primary/30',
            isLocked
              ? 'cursor-not-allowed'
              : 'cursor-grab hover:text-primary/60 active:cursor-grabbing',
          )}
          {...attributes}
          {...(isLocked ? {} : listeners)}
          aria-label={t('jobs.questions.drag')}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <circle cx="9" cy="6" r="1.5" />
            <circle cx="15" cy="6" r="1.5" />
            <circle cx="9" cy="12" r="1.5" />
            <circle cx="15" cy="12" r="1.5" />
            <circle cx="9" cy="18" r="1.5" />
            <circle cx="15" cy="18" r="1.5" />
          </svg>
        </button>

        <div className="flex flex-1 flex-col gap-3">
          <div className="flex items-start gap-2">
            <span className="mt-2 shrink-0 text-xs font-medium text-primary/40">
              {index + 1}.
            </span>
            <Textarea
              value={question.text}
              onChange={(e) => onUpdate(question.id, { text: e.target.value })}
              placeholder={t('jobs.questions.textPlaceholder')}
              className="min-h-[72px] flex-1 resize-none"
              disabled={isLocked}
            />
          </div>

          {jobLanguage === 'both' && (
            <div className="flex flex-col gap-1.5 pl-6">
              <div className="flex items-center justify-between">
                <span className="text-xs text-primary/50">
                  {t('jobs.translations.english')}
                </span>
                <TranslateButton
                  jobId={jobId}
                  sourceLanguage="sv"
                  targetLanguage="en"
                  text={question.text}
                  isHtml={false}
                  disabled={isLocked || !question.text.trim()}
                  onTranslated={(text) =>
                    onUpdate(question.id, {
                      textTranslation: text,
                      textTranslationIsDraft: true,
                    })
                  }
                />
              </div>
              {question.textTranslationIsDraft && (
                <div className="flex items-center gap-1.5 rounded-md bg-amber-50 px-2.5 py-1.5 text-xs text-amber-700 dark:bg-amber-950/30 dark:text-amber-400">
                  {t('jobs.translations.draftHint')}
                </div>
              )}
              <Textarea
                value={question.textTranslation}
                onChange={(e) =>
                  onUpdate(question.id, { textTranslation: e.target.value })
                }
                placeholder={t('jobs.questions.textPlaceholder')}
                className={`min-h-[60px] resize-none text-sm ${question.textTranslationIsDraft ? 'border-amber-400 ring-1 ring-amber-400/30' : ''}`}
                disabled={isLocked}
              />
            </div>
          )}

          <Input
            value={question.competency}
            onChange={(e) =>
              onUpdate(question.id, { competency: e.target.value })
            }
            placeholder={t('jobs.questions.competencyPlaceholder')}
            className="text-sm"
            disabled={isLocked}
          />

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() =>
                onUpdate(question.id, {
                  followUpsExpanded: !question.followUpsExpanded,
                })
              }
              className="text-xs text-primary/50 hover:text-primary"
            >
              {question.followUpsExpanded
                ? t('jobs.questions.hideFollowUps')
                : t('jobs.questions.showFollowUps', {
                    count: question.followUps.length,
                  })}
            </button>
            {!isLocked && (
              <>
                <span className="text-primary/20">·</span>
                <button
                  type="button"
                  onClick={() => onSaveToBank(question.id)}
                  className="text-xs text-primary/50 hover:text-primary"
                >
                  {t('jobs.questions.saveToBank')}
                </button>
                <span className="text-primary/20">·</span>
                <button
                  type="button"
                  onClick={() => onRemove(question.id)}
                  className="text-xs text-destructive/70 hover:text-destructive"
                >
                  {t('jobs.questions.remove')}
                </button>
              </>
            )}
          </div>

          {question.followUpsExpanded && (
            <div className="flex flex-col gap-2 border-l-2 border-border pl-4">
              {question.followUps.map((followUp, fIndex) => (
                <div key={followUp.id} className="flex flex-col gap-2">
                  <div className="flex items-start gap-2">
                    <span className="mt-2 shrink-0 text-xs text-primary/30">
                      {index + 1}.{fIndex + 1}
                    </span>
                    <Textarea
                      value={followUp.text}
                      onChange={(e) =>
                        onUpdateFollowUp(question.id, followUp.id, {
                          text: e.target.value,
                        })
                      }
                      placeholder={t('jobs.questions.followUpPlaceholder')}
                      className="min-h-[60px] flex-1 resize-none text-sm"
                      disabled={isLocked}
                    />
                    {!isLocked && (
                      <button
                        type="button"
                        onClick={() =>
                          onRemoveFollowUp(question.id, followUp.id)
                        }
                        className="mt-2 text-destructive/60 hover:text-destructive"
                        aria-label={t('jobs.questions.removeFollowUp')}
                      >
                        ×
                      </button>
                    )}
                  </div>
                  <Input
                    value={followUp.competency}
                    onChange={(e) =>
                      onUpdateFollowUp(question.id, followUp.id, {
                        competency: e.target.value,
                      })
                    }
                    placeholder={t('jobs.questions.competencyPlaceholder')}
                    className="ml-6 text-sm"
                    disabled={isLocked}
                  />
                </div>
              ))}
              {!isLocked && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => onAddFollowUp(question.id)}
                >
                  {t('jobs.questions.addFollowUp')}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

type JobQuestionsEditorProps = {
  readonly jobId: Id<'jobs'>
  readonly jobLanguage: string
  readonly initialQuestions: readonly JobQuestionNode[]
  readonly isLocked: boolean
}

export const JobQuestionsEditor = ({
  jobId,
  jobLanguage,
  initialQuestions,
  isLocked,
}: JobQuestionsEditorProps) => {
  const { t } = useTranslation()
  const [questions, setQuestions] = useState<QuestionDraft[]>(() =>
    fromNodes(initialQuestions),
  )
  const [saved, setSaved] = useState(false)

  const { mutateAsync: saveQuestions, isPending } = useMutation({
    mutationFn: useConvexMutation(api.features.jobs.jobs.upsertMyJobQuestions),
  })

  const { mutateAsync: saveQuestionToBank } = useMutation({
    mutationFn: useConvexMutation(
      api.features.jobs.question_bank.saveMyQuestionToBank,
    ),
  })

  const { data: bankItems = [] } = useQuery(
    convexQuery(api.features.jobs.question_bank.listMyQuestionBankItems, {}),
  )

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    setQuestions((prev) => {
      const oldIndex = prev.findIndex((q) => q.id === active.id)
      const newIndex = prev.findIndex((q) => q.id === over.id)
      return arrayMove(prev, oldIndex, newIndex)
    })
  }

  const handleUpdate = (id: string, patch: Partial<QuestionDraft>) => {
    setQuestions((prev) =>
      prev.map((q) => (q.id === id ? { ...q, ...patch } : q)),
    )
  }

  const handleRemove = (id: string) => {
    setQuestions((prev) => prev.filter((q) => q.id !== id))
  }

  const handleUpdateFollowUp = (
    questionId: string,
    followUpId: string,
    patch: Partial<FollowUpDraft>,
  ) => {
    setQuestions((prev) =>
      prev.map((q) =>
        q.id !== questionId
          ? q
          : {
              ...q,
              followUps: q.followUps.map((f) =>
                f.id === followUpId ? { ...f, ...patch } : f,
              ),
            },
      ),
    )
  }

  const handleAddFollowUp = (questionId: string) => {
    setQuestions((prev) =>
      prev.map((q) =>
        q.id !== questionId
          ? q
          : { ...q, followUps: [...q.followUps, createEmptyFollowUp()] },
      ),
    )
  }

  const handleRemoveFollowUp = (questionId: string, followUpId: string) => {
    setQuestions((prev) =>
      prev.map((q) =>
        q.id !== questionId
          ? q
          : {
              ...q,
              followUps: q.followUps.filter((f) => f.id !== followUpId),
            },
      ),
    )
  }

  const handleAddFromBank = (item: Doc<'questionBank'>) => {
    setQuestions((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        text: item.text,
        competency: item.competency ?? '',
        textTranslation: '',
        textTranslationIsDraft: false,
        followUps: [],
        followUpsExpanded: false,
      },
    ])
  }

  const handleSaveToBank = async (questionId: string) => {
    const question = questions.find((q) => q.id === questionId)
    if (!question?.text.trim()) return

    await saveQuestionToBank({
      text: question.text.trim(),
      competency: question.competency.trim() || undefined,
    })
  }

  const handleSave = async () => {
    await saveQuestions({
      jobId,
      questions: questions.map((q, i) => ({
        text: q.text,
        competency: q.competency || undefined,
        order: i,
        textTranslations: q.textTranslation
          ? { en: q.textTranslation }
          : undefined,
        followUps: q.followUps.map((f, fi) => ({
          text: f.text,
          competency: f.competency || undefined,
          order: fi,
        })),
      })),
    })
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  const handleAddGeneratedQuestions = (
    generated: ReadonlyArray<{
      readonly text: string
      readonly competency: string
    }>,
  ) => {
    setQuestions((prev) => [
      ...prev,
      ...generated.map((q) => ({
        id: crypto.randomUUID(),
        text: q.text,
        competency: q.competency,
        textTranslation: '',
        textTranslationIsDraft: false,
        followUps: [],
        followUpsExpanded: false,
      })),
    ])
  }

  return (
    <div className="flex flex-col gap-4">
      {isLocked && (
        <div className="flex items-center gap-2 rounded-md border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-800 dark:border-orange-800/40 dark:bg-orange-900/20 dark:text-orange-400">
          <span>🔒</span>
          <span>{t('jobs.locked.notice')}</span>
        </div>
      )}
      {!isLocked && (
        <GenerateQuestionsPanel
          jobId={jobId}
          onAddQuestions={handleAddGeneratedQuestions}
        />
      )}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={questions.map((q) => q.id)}
          strategy={verticalListSortingStrategy}
        >
          {questions.map((question, index) => (
            <SortableQuestion
              key={question.id}
              jobId={jobId}
              jobLanguage={jobLanguage}
              question={question}
              index={index}
              isLocked={isLocked}
              onUpdate={handleUpdate}
              onRemove={handleRemove}
              onUpdateFollowUp={handleUpdateFollowUp}
              onAddFollowUp={handleAddFollowUp}
              onRemoveFollowUp={handleRemoveFollowUp}
              onSaveToBank={handleSaveToBank}
            />
          ))}
        </SortableContext>
      </DndContext>

      {questions.length === 0 && (
        <p className="py-8 text-center text-sm text-primary/40">
          {t('jobs.questions.empty')}
        </p>
      )}

      {!isLocked && (
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() =>
              setQuestions((prev) => [...prev, createEmptyQuestion()])
            }
          >
            {t('jobs.questions.addQuestion')}
          </Button>
          {bankItems.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger>
                <Button type="button" variant="outline">
                  {t('jobs.questions.addFromBank')}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="max-h-72 w-80 overflow-y-auto"
              >
                {bankItems.map((item) => (
                  <DropdownMenuItem
                    key={item._id}
                    onSelect={() => handleAddFromBank(item)}
                    className="flex flex-col items-start gap-0.5"
                  >
                    <span className="line-clamp-2 text-sm">{item.text}</span>
                    {item.competency && (
                      <span className="text-xs text-primary/50">
                        {item.competency}
                      </span>
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <Button type="button" onClick={handleSave} disabled={isPending}>
            {isPending ? t('jobs.questions.saving') : t('jobs.questions.save')}
          </Button>
          {saved && (
            <span className="text-sm text-primary/60">
              {t('jobs.questions.saved')}
            </span>
          )}
        </div>
      )}
    </div>
  )
}
